<!--
 * @Author: Antigravity
 * @Date: 2026-03-23
 * @Description: 游客分析-出行方式
-->

<template>
  <div class="w-full flex flex-col">
    <div class="w-full flex items-end justify-between my-16">
      <div class="text-16 font-bold text-black-primary">出行方式</div>
    </div>

    <div class="card flex flex-col p-16">
      <div v-if="loading" class="state-text">加载中...</div>
      <div v-else-if="!list.length" class="state-text">暂无数据</div>
      <template v-else>
        <div ref="chartEl" class="w-full h-202"></div>

        <div class="mt-16 px-16">
          <div class="flex flex-wrap gap-16 justify-between">
            <div v-for="item in legendItems" :key="item.name" class="legend-item">
              <div class="flex items-center gap-8 mb-4">
                <i class="dot" :style="{ background: item.color }"></i>
                <span class="text-14 font-bold text-[#1E2226]">{{ item.name }}</span>
                <span class="text-14 font-bold text-[#1E2226]">{{ item.ratio }}</span>
              </div>
              <div class="ml-20 text-12 text-[#999]">
                {{ formatNumber(item.total) }}件
              </div>
            </div>
          </div>
        </div>

        <div class="mt-20 px-16 text-12 text-[#999] leading-18">
          因部分投诉件游客主动撤销时未录入出行方式，所有总计数据（团队+个人）可能小于投诉总量。
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts';
import APIS from "@/service/modules/complaintApis/modules/visualization.js";
import visualFilterMixin from "../../mixins/visualFilterMixin";

export default {
  name: "TripModeCountCell",
  mixins: [visualFilterMixin],
  data() {
    return {
      chart: null,
      loading: false,
      apiTotal: 0,
      list: [],
      _onResize: null,
    };
  },
  computed: {
    legendItems() {
      // 颜色映射：根据 tripMode 或名称来确保颜色一致
      // 跟团游使用绿色 (#36B374)，自由行使用蓝色 (#4083E7)
      const colorMap = {
        '跟团游': '#36B374',
        '自由行': '#4083E7'
      };
      return this.list.map((it) => ({
        ...it,
        color: colorMap[it.name] || '#FF8C19'
      }));
    }
  },
  watch: {
    list: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderChart());
      }
    }
  },
  mounted() {
    this.ensureChart();
  },
  beforeDestroy() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this.chart) this.chart.dispose();
  },
  methods: {
    formatNumber(n) {
      if (!n && n !== 0) return "0";
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    ensureChart() {
      const el = this.$refs.chartEl;
      if (!el) return;
      // v-if 切换后 DOM 会重建，旧 chart 实例的 DOM 不再匹配
      if (this.chart) {
        if (typeof this.chart.getDom === 'function' && this.chart.getDom() !== el) {
          this.chart.dispose();
          this.chart = null;
        } else {
          return;
        }
      }
      this.chart = echarts.init(el);
      this._onResize = () => this.chart && this.chart.resize();
      window.addEventListener('resize', this._onResize, { passive: true });
    },
    renderChart() {
      this.ensureChart();
      if (!this.chart) return;

      const totalValue = this.apiTotal || 1;
      const dataItems = this.legendItems.map(it => ({
        value: it.total,
        name: it.name,
        itemStyle: { color: it.color }
      }));

      // 添加透明部分占位实现半圆
      const listSum = dataItems.reduce((s, i) => s + i.value, 0);
      const placeholderValue = Math.max(listSum, totalValue);

      const option = {
        backgroundColor: '#fff',
        title: {
          left: 'center',
          top: '45%',
          text: '投诉总量',
          subtext: `{count|${this.formatNumber(this.apiTotal)}} 件`,
          textStyle: { color: '#1E2226', fontSize: 14, fontWeight: 600 },
          subtextStyle: {
            rich: {
              count: { fontSize: 24, fontWeight: 700, color: '#1E2226', padding: [4, 0] }
            }
          }
        },
        tooltip: {
          trigger: 'item',
          confine: true
        },
        series: [
          {
            type: 'pie',
            name: '出行方式',
            radius: ['65%', '100%'],
            center: ['50%', '65%'],
            startAngle: 180,
            endAngle: 360,
            label: { show: false },
            itemStyle: { borderColor: '#fff', borderWidth: 4 },
            data: [
              ...dataItems,
              {
                value: placeholderValue,
                name: '__hide__',
                itemStyle: { color: 'transparent' },
                tooltip: { show: false },
                emphasis: { disabled: true }
              }
            ]
          }
        ]
      };
      this.chart.setOption(option, true);
    },
    onLoad() {
      const { areaId, beginTime, endTime, rule, channel } = this.filterParams;
      const params = {
        cityId: areaId,
        startDate: beginTime,
        endDate: endTime,
        rule: rule,
        channel: channel
      };

      this.loading = true;
      APIS.getTripModeCount(params).then(res => {
        if (res && res.ret === 0) {
          const data = res.data || {};
          this.apiTotal = data.total || 0;
          this.list = Array.isArray(data.list) ? data.list : [];
        }
      }).finally(() => {
        this.loading = false;
        this.$nextTick(() => this.renderChart());
      });
    }
  }
};
</script>

<style lang="less" scoped>
.card {
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
}

.state-text {
  width: 100%;
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
}

.legend-item {
  min-width: 45%;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
</style>
